// Central price catalogue — hand-picked round prices per currency.
// Amounts are the real charge amounts; Stripe holds matching currency_options.

export const CURRENCIES = ["EUR", "USD", "GBP", "CAD", "AUD"] as const;
export type Currency = (typeof CURRENCIES)[number];

export const CURRENCY_SYMBOL: Record<Currency, string> = {
  EUR: "€",
  USD: "$",
  GBP: "£",
  CAD: "CA$",
  AUD: "A$",
};

export const CURRENCY_LABEL: Record<Currency, string> = {
  EUR: "EUR €",
  USD: "USD $",
  GBP: "GBP £",
  CAD: "CAD CA$",
  AUD: "AUD A$",
};

export type ProductKey =
  | "map_single"
  | "map_gift_pair"
  | "map_gift_single"
  | "couples_map";

export const PRICES: Record<ProductKey, Record<Currency, number>> = {
  map_single:      { EUR: 19.90, USD: 21.99, GBP: 17.99, CAD: 29.99, AUD: 32.99 },
  map_gift_pair:   { EUR: 29.90, USD: 32.99, GBP: 26.99, CAD: 44.99, AUD: 49.99 },
  map_gift_single: { EUR: 19.90, USD: 21.99, GBP: 17.99, CAD: 29.99, AUD: 32.99 },
  couples_map:     { EUR: 49.00, USD: 54.99, GBP: 44.99, CAD: 74.99, AUD: 84.99 },
};

/** Cart product ids used for Map items → catalogue key. */
export const CART_ID_TO_PRODUCT: Record<string, ProductKey> = {
  "zemelapis-single": "map_single",
  "zemelapis-gift-pair": "map_gift_pair",
  "zemelapis-gift-single": "map_gift_single",
};

export const isCurrency = (v: unknown): v is Currency =>
  typeof v === "string" && (CURRENCIES as readonly string[]).includes(v);

/** Format an amount, trimming a trailing ".00" only for whole euro prices. */
export function formatMoney(amount: number, currency: Currency): string {
  const symbol = CURRENCY_SYMBOL[currency];
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  return `${symbol}${hasCents ? amount.toFixed(2) : String(Math.round(amount))}`;
}

export function getPrice(product: ProductKey, currency: Currency): number {
  return PRICES[product][currency];
}

export function formatPrice(product: ProductKey, currency: Currency): string {
  return formatMoney(getPrice(product, currency), currency);
}

/** Price of a cart item in the chosen currency; falls back to the stored EUR price. */
export function cartItemPrice(
  item: { id: string; price: number },
  currency: Currency
): number {
  const key = CART_ID_TO_PRODUCT[item.id];
  if (key) return getPrice(key, currency);
  if (currency === "EUR") return item.price;
  // Unknown product (books/lessons are disabled here) — keep the stored amount.
  return item.price;
}
